/**
 * Validate that a redirect path is safe (relative, same-origin).
 * Prevents open redirect attacks by only allowing local paths.
 *
 * - Must start with `/`
 * - Must not start with `//` (protocol-relative URLs)
 * - Must not contain `://` (absolute URLs)
 */
export const isSafeRedirect = (p: string): boolean =>
  p.startsWith('/') && !p.startsWith('//') && !p.includes('://');

/**
 * Resolve a raw redirect query param into a safe path.
 * Falls back to the provided default if the redirect is missing or unsafe.
 *
 * Accepts `string[]` because Next's `router.query`/`req.query` yields an array
 * for repeated params (e.g. `?redirect=/a&redirect=/b`); we normalize to the
 * first value so `isSafeRedirect` never receives an array.
 */
export const safeRedirect = (
  raw: string | string[] | undefined,
  fallback: string,
): string => {
  const value = Array.isArray(raw) ? raw[0] : raw;
  return value && isSafeRedirect(value) ? value : fallback;
};
